import { FC } from "react";
import { useTranslation } from "react-i18next";
import { IoIosArrowForward } from "react-icons/io";
import { HeadersFunction, MetaFunction, redirect, useLoaderData, useParams } from "react-router";
import BreadCrubComponent from "~/components/shared-components/BreadCrubComponent";
import DisplayPart from "~/components/shared-components/DisplayPart";
import NodataFound from "~/components/shared-components/NoDataFound";
import Circle from "~/components/video-grid/Circle";
import Rectangular from "~/components/video-grid/Rectangular";
import Square from "~/components/video-grid/Square";
import { DifferentViewComponentProps } from "~/components/video-grid/type";
import Vertical from "~/components/video-grid/Vertical";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import useScreenSize from "~/hook/useScreenSize";
import { VideoCategoryNodeData } from "~/interface/video-category/type";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { cn } from "~/lib/utils";
import { allowedCategories, CACHED_PAGE, CATEGORY_LABELS, COLORS, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { isBrowser } from "~/utils/envirement";
import { capitalize, computeEtagFromData, homeRouteString } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

const COMPONENT_MAPPING: Record<string, FC<DifferentViewComponentProps>> = {
  circle: Circle,
  square: Square,
  vertical: Vertical,
  rectangular: Rectangular,
};

interface LoaderData {
  title: string;
  nodeData: VideoCategoryNodeData;
}

export const meta: MetaFunction = ({ location, params, data }) => {
  const { lang, video_category } = params;
  const pathname = location?.pathname || "/";
  const meta = getPageMeta("video_category", lang || "en");

  return metaService({
    title: `${capitalize(video_category ?? "")} | Swaminarayanbhagwan.org`,
    description: meta.metaDescription,
    ogTitle: `${capitalize(video_category ?? "")} | Swaminarayanbhagwan.org`,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage ?? FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth ?? "1200",
    ogHeight: meta.ogImageHeight ?? "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader = async ({ params, request }: { params: { lang: string; video_category: string }; request: Request }): Promise<any> => {
  const { lang, video_category } = params;

  const isValid = /^[a-z-]+$/.test(video_category ?? "") && allowedCategories.includes(video_category!);

  if (!isValid) {
    throw redirect(params.lang ? `/${params.lang}/` : `/`);
  }

  const apiService = new ApiService(lang || LANG.english);

  const videoCategoryResponse = await apiService.getVideoCategory(video_category!);
  if (!videoCategoryResponse.length) {
    throw new Response("Not Found", { status: 404 });
  }

  const langKey = lang === LANG.english ? "en" : lang === LANG.gujarati ? "gu" : "en";

  const etag = computeEtagFromData({
    videoCategoryResponse,
  });

  const ifNoneMatch = request.headers.get("If-None-Match");

  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(
    {
      title: CATEGORY_LABELS[video_category.toLocaleLowerCase()] ? CATEGORY_LABELS[video_category][langKey] : capitalize(video_category || ""),
      nodeData: videoCategoryResponse || [],
    },
    {
      headers: {
        ...CACHED_PAGE.PLAYLIST.HEADERS,
        ETag: etag,
      },
    },
  );
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

export const handle = {
  breadcrumb: (match: any) => {
    const lang = match.params.lang;
    return (
      <BreadCrubComponent
        breadCrub={[
          {
            path: homeRouteString(lang).path,
            title: homeRouteString(lang).home,
          },
        ]}
      />
    );
  },
  breadcrumbSchema: (match: any) => [
    { name: "Home", path: "/" },
    { name: match.data?.title || match.params.video_category, path: `/${match.params.video_category}` },
  ],
};

const VideoCategoryPage = () => {
  const { video_category } = useParams();
  const { nodeData } = useLoaderData<LoaderData>();
  const { semiBold, semiBoldInter, mediumInter } = useFontStyle();
  const { LocalizedLink } = useLocale();
  const screenSize = useScreenSize();
  const { t } = useTranslation();

  const getItemCount = (screenSize: string, dispStyle: string) => {
    const zoom = isBrowser() ? window.devicePixelRatio : 1;
    const counts = {
      mobile: { circle: 10, square: 10, rectangular: 10, vertical: 10 },
      tablet: { circle: 3, square: 6, rectangular: 3, vertical: 6 },
      laptop: { circle: 5, square: 6, rectangular: 5, vertical: 8 },
      desktop: { circle: 7, square: zoom >= 1.25 ? 7 : 8, rectangular: 5, vertical: 8 },
    };

    return counts[screenSize as keyof typeof counts]?.[dispStyle as keyof typeof counts.mobile] || 5;
  };

  return (
    <div className="px-3 md:px-5 lg:px-0 -mt-5 mb-5" style={{ scrollbarGutter: "stable" }}>
      {nodeData?.map((section, sectionIndex) => {
        if (!section || !section?.data || section.data.length === 0) return null;
        const Component = COMPONENT_MAPPING[section?.disp_style] || Square;
        const itemCount = getItemCount(screenSize, section?.disp_style || "rectangular");

        return (
          <div key={sectionIndex} className="xs:mb-3 md:mb-10">
            <LocalizedLink
              to={`/${video_category}/${section.url_slug || ""}`}
              // query={{ ["sort-by"]: "relevance", ["sort-direction"]: "asc" }}
              className="w-full flex justify-between items-center xs:mb-1 md:mb-2"
            >
              <div className={cn("flex-1 flex gap-x-2 items-center max-w-[70vw] ", semiBold)}>
                <p className="body_text_l line-clamp-2 text-textHeadingColor truncate">{section.name}</p>
                {section?.count ? <DisplayPart number={section?.count || 0} /> : null}
              </div>

              <div className="flex">
                <div className="flex items-center justify-center md:gap-2 text-textSecondaryColor">
                  <span className={cn("body_text_m text-textHeadingColor cursor-pointer tracking-wide capitalize", mediumInter)}>
                    {/* {t("common.see_more", { count: section?.count || 0 })} */}
                    {t("common.view_all")}
                  </span>
                  <IoIosArrowForward size={18} color={COLORS.iconOrange} />
                </div>
              </div>
            </LocalizedLink>
            <div>
              <Component
                data={section?.data?.slice(0, itemCount).map((item) => ({
                  id: item.id,
                  name: item.name,
                  duration_sec: item.duration_sec,
                  total_playlist_item: item.count || 0,
                  videoID: item?.video_id || "",
                  disp_style: item?.disp_style,
                  record_date: item?.record_date || "",
                  url_slug: item.url_slug,
                  img: item?.img,
                  vertical_img: item?.vertical_img,
                  square_img: item?.square_img,
                  node_type: item.node_type,
                  media_type: item.media_type,
                  count: item.count,
                }))}
                section_name={section.name}
                url_slug={section.url_slug}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VideoCategoryPage;

export const ErrorBoundary = () => {
  return (
    <div className="not-found-data">
      <NodataFound text="No data found" />
    </div>
  );
};
